import { useState } from "react";
import { useParams, Link, useLocation } from "wouter";
import { useGetDealById, useRedeemDeal } from "@workspace/api-client-react";
import { useSEO } from "@/hooks/use-seo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useQueryClient } from "@tanstack/react-query";
import { ShareBar } from "@/components/ShareBar";
import { GoogleAd } from "@/components/GoogleAd";
import { useAnalytics } from "@/hooks/use-analytics";

function DaysLeft({ endDate }: { endDate: string }) {
  const today = new Date();
  const end = new Date(endDate);
  const diff = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return <span className="text-xs text-slate-400">Expired</span>;
  if (diff === 0) return <span className="text-sm font-semibold text-red-600">⚠️ Expires today!</span>;
  if (diff <= 3) return <span className="text-sm font-semibold text-orange-600">⏰ {diff} days left</span>;
  return <span className="text-sm text-slate-500">Valid until {new Date(endDate).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}</span>;
}


export default function DealDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useGetDealById(id!);
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const { mutate: redeem, isPending: redeeming } = useRedeemDeal();
  const { track } = useAnalytics();
  const [redeemed, setRedeemed] = useState<string | null>(null);

  const myRedemption = redeemed ?? data?.myRedemption ?? null;
  const redemptionCount = (data?.redemptionCount ?? 0) + (redeemed && !data?.myRedemption ? 1 : 0);
  const dealUrl = typeof window !== "undefined" ? window.location.href : "";

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long" });
  const validityText = data?.startDate && data?.endDate
    ? `Valid ${fmtDate(data.startDate)} – ${fmtDate(data.endDate)}`
    : data?.endDate
      ? `Valid until ${fmtDate(data.endDate)}`
      : data?.startDate
        ? `Valid from ${fmtDate(data.startDate)}`
        : null;

  useSEO({
    title: data ? `${data.title} — ${data.businessName}` : undefined,
    description: data?.description ?? undefined,
    image: data?.imageUrl ?? undefined,
    jsonLd: data ? {
      "@context": "https://schema.org",
      "@type": "Offer",
      "name": data.title,
      "description": data.description,
      "offeredBy": { "@type": "Organization", "name": data.businessName },
      "url": `https://www.ayrshirehub.co.uk/deals/${data.id}`,
      "priceCurrency": "GBP",
      ...(data.endDate ? { "availabilityEnds": data.endDate } : {}),
      ...(data.imageUrl ? { "image": data.imageUrl } : {}),
    } : undefined,
  });

  const handleRedeem = () => {
    if (!user) {
      const returnTo = typeof window !== "undefined" ? window.location.pathname : `/deals/${id}`;
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
      return;
    }
    redeem(
      { id: Number(id) },
      {
        onSuccess: (res) => {
          setRedeemed(res.redeemedAt);
          track("deal_redeemed", { screen: "deal-detail", metadata: { dealId: Number(id), title: data?.title, businessName: data?.businessName } });
          void qc.invalidateQueries({ queryKey: ["getDealById", Number(id)] });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <div className="h-52 bg-slate-200 rounded-2xl animate-pulse mb-6" />
        <div className="space-y-3">
          <div className="h-8 bg-slate-200 rounded animate-pulse w-3/4" />
          <div className="h-4 bg-slate-200 rounded animate-pulse w-1/2" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <p className="text-5xl mb-4">🏷️</p>
        <p className="text-slate-500 font-medium">Deal not found.</p>
        <Link href="/deals"><Button variant="outline" className="mt-4">Back to Deals</Button></Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-slate-400 mb-6">
          <Link href="/" className="hover:text-primary transition-colors">Home</Link>
          <span aria-hidden="true">/</span>
          <Link href="/deals" className="hover:text-primary transition-colors">Deals</Link>
          <span aria-hidden="true">/</span>
          <span className="text-slate-600 truncate max-w-[200px]" aria-current="page">{data.title}</span>
        </nav>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          {data.imageUrl ? (
            <div className="relative h-52 sm:h-64">
              <img src={data.imageUrl} alt={data.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute top-4 left-4">
                <span className="bg-secondary text-secondary-foreground text-sm font-bold px-3 py-1.5 rounded-full">DEAL</span>
              </div>
            </div>
          ) : (
            <div className="h-32 bg-gradient-to-br from-secondary/20 to-amber-50 flex items-center justify-center">
              <span className="text-6xl">🏷️</span>
            </div>
          )}

          <div className="p-6 sm:p-8 space-y-5">
            <div>
              <div className="flex flex-wrap gap-2 mb-2">
                {data.featured && <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full">Featured</span>}
                {data.category && <span className="bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full capitalize">{data.category}</span>}
              </div>
              <p className="text-sm font-semibold text-slate-500 mb-1">{data.businessName}</p>
              <h1 className="text-2xl font-bold text-slate-800 leading-snug">{data.title}</h1>
            </div>

            {/* Redemption count */}
            {redemptionCount > 0 && (
              <div className="flex items-center gap-1.5 text-sm text-slate-500">
                <span className="text-base">🎟️</span>
                <span><strong className="text-slate-700">{redemptionCount}</strong> {redemptionCount === 1 ? "person has" : "people have"} redeemed this deal</span>
              </div>
            )}

            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 space-y-2.5">
              <div className="flex items-center gap-2 text-sm">
                <span>📍</span><span className="text-slate-700">{data.town}</span>
              </div>
              {data.startDate && (
                <div className="flex items-center gap-2 text-sm">
                  <span>📅</span><span className="text-slate-700">Valid from {new Date(data.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}</span>
                </div>
              )}
              {data.endDate && (
                <div className="flex items-center gap-2">
                  <span className="text-sm">⏰</span>
                  <DaysLeft endDate={data.endDate} />
                </div>
              )}
            </div>

            <div>
              <h2 className="font-semibold text-slate-800 mb-2">About this deal</h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{data.description}</p>
            </div>

            <div className="my-2">
              <GoogleAd />
            </div>

            {/* How to redeem — always visible, BEFORE the customer commits */}
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5 space-y-3">
              <h2 className="font-bold text-amber-900 flex items-center gap-2 text-base">
                🎯 How to redeem
              </h2>
              <p className="text-amber-900/90 leading-relaxed whitespace-pre-wrap text-sm">
                {data.redemptionInstructions?.trim()
                  ? data.redemptionInstructions
                  : `Redeem this deal here, then show the confirmation to staff at ${data.businessName} to claim it.`}
              </p>

              <ul className="space-y-1.5 pt-3 border-t border-amber-200 text-sm text-amber-900/80">
                {validityText && (
                  <li className="flex items-start gap-2">
                    <span>📅</span><span>{validityText}</span>
                  </li>
                )}
                <li className="flex items-start gap-2">
                  <span>🎟️</span>
                  <span><strong className="font-semibold">One redemption per account.</strong> Redeeming is permanent and can't be undone, so only redeem when you're ready to use it.</span>
                </li>
              </ul>
            </div>

            {/* Redeem CTA or confirmation */}
            {myRedemption ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 space-y-3">
                <p className="text-emerald-700 font-bold flex items-center gap-2 text-base">
                  ✅ You've redeemed this deal!
                </p>
                <p className="text-emerald-600 text-sm">
                  Redeemed on {new Date(myRedemption).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                </p>
                <p className="text-emerald-600 text-sm">
                  Follow the "How to redeem" steps above to claim it in store.
                </p>
                <div className="pt-1 flex flex-col gap-2">
                  <Link href={`/businesses?search=${encodeURIComponent(data.businessName)}`} className="block">
                    <button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2.5 px-4 rounded-lg transition-colors">
                      📍 Find {data.businessName}
                    </button>
                  </Link>
                  <Link href="/deals" className="block text-center text-sm text-emerald-600 hover:text-emerald-800 font-medium py-1">
                    Browse more deals
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Button
                  onClick={handleRedeem}
                  disabled={redeeming}
                  className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold text-base py-6 rounded-xl shadow-sm"
                >
                  {redeeming ? "Redeeming…" : "🏷️ Tap to Redeem"}
                </Button>
                {!user && (
                  <p className="text-center text-xs text-slate-400">You'll need to log in or sign up to redeem</p>
                )}
              </div>
            )}

            {/* Share + find more */}
            <div className="pt-4 border-t border-slate-100 space-y-3">
              <ShareBar url={dealUrl} title={`${data.title} — ${data.businessName}`} />
              <Link href={`/businesses?search=${encodeURIComponent(data.businessName)}`} className="block">
                <Button variant="outline" className="w-full">
                  More from {data.businessName}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
